import { ChangeDetectionStrategy, Component, computed, effect, EffectRef, inject, input, InputSignal, ResourceRef, signal, Signal, WritableSignal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { PageHeaderComponent, SegmentedProgressBarComponent } from '@libs/common/components';
import { IconComponent } from '@libs/components/icons';
import type { SrmTicketModel, SrmTicketStatusModel, SrmTicketStatusType } from '@libs/services/models';
import { SrmTrackingStatus } from '@libs/shared/models';
import { Observable } from 'rxjs';
import { SrmTicketItemComponent } from '../srm-ticket-item';
import { SrmService, SrmTrackingService } from '../services';
import { SrmDetailsSkeletonComponent } from './srm-details-skeleton';

const STATUS_TRACKING_MAP: Record<SrmTicketStatusType, SrmTrackingStatus> = {
  OPEN: 'open',
  IN_PROGRESS: 'in-progress',
  COMPLETED: 'closed',
};

const STATUS_STEPS: SrmTicketStatusType[] = ['OPEN', 'IN_PROGRESS', 'COMPLETED'];

const STATUS_LABELS: Record<SrmTicketStatusType, string> = {
  OPEN: 'offen',
  IN_PROGRESS: 'in Bearbeitung',
  COMPLETED: 'geschlossen',
};

@Component({
  selector: 'ui-srm-details',
  imports: [
    IconComponent,
    PageHeaderComponent,
    SegmentedProgressBarComponent,
    SrmDetailsSkeletonComponent,
    SrmTicketItemComponent,
  ],
  templateUrl: './srm-details.component.html',
  styleUrls: ['./srm-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SrmDetailsComponent {
  public readonly srmService: SrmService = inject(SrmService);
  public readonly ticketIdentifier: InputSignal<string> = input.required();

  public readonly steps: SrmTicketStatusType[] = STATUS_STEPS;
  public readonly statusLabels: Record<SrmTicketStatusType, string> = STATUS_LABELS;

  public readonly ticketResource: ResourceRef<SrmTicketModel | undefined> = rxResource({
    params: (): string => this.ticketIdentifier(),
    stream: ({ params }: { params: string }): Observable<SrmTicketModel | undefined> =>
      this.srmService.getTicket(params),
  });

  public readonly ticket: Signal<SrmTicketModel | undefined> = computed((): SrmTicketModel | undefined => this.ticketResource.value());

  public readonly currentStatus: Signal<SrmTicketStatusType> = computed((): SrmTicketStatusType => this.ticket()?.status ?? 'OPEN');

  public readonly hasCompleted: Signal<boolean> = computed((): boolean => this.statusHistory().some((historyEntry) => historyEntry.status === 'COMPLETED'));

  public readonly solutionComment: Signal<string | undefined> = computed((): string | undefined => {
    const item: SrmTicketStatusModel | undefined = [...this.statusHistory()].reverse().find((historyEntry: SrmTicketStatusModel) => historyEntry.status === 'COMPLETED');

    return item?.comment ?? undefined;
  });

  private readonly statusHistory: Signal<readonly SrmTicketStatusModel[]> =
    computed((): readonly SrmTicketStatusModel[] => this.ticket()?.statusHistory ?? []);

  private readonly srmTrackingService: SrmTrackingService = inject(SrmTrackingService);
  private readonly trackedTicketIdentifier: WritableSignal<string | undefined> = signal<string | undefined>(undefined);

  private readonly trackingEffect: EffectRef = effect((): void => {
    const ticket: SrmTicketModel | undefined = this.ticket();
    const ticketIdentifier: string = this.ticketIdentifier();

    if (!ticket || this.trackedTicketIdentifier() === ticketIdentifier) {
      return;
    }

    this.trackedTicketIdentifier.set(ticketIdentifier);

    const [ category, issue, topic ]: (string | undefined)[] = ticket.selectedCategories;

    this.srmTrackingService.trackSrmDetail({
      srmStatus: STATUS_TRACKING_MAP[ticket.status],
      location: `/tickets/${ticketIdentifier}`,
      srmCategory: category ?? null,
      srmIssue: issue ?? null,
      srmTopic: topic ?? null,
    });
  });
}
